import { Component, ErrorInfo, ReactNode } from 'react';
import { Delimiters } from '../utils/delimiters';
import { generateRawErrorCode, getCurrentLanguage, postJsError } from './errorUtils';

export interface ComponentErrorBoundaryProps {
  fallbackErrorComponent: (errorState: ComponentErrorBoundaryState, clearState: () => void) => ReactNode;
  onComponentDidCatch?: (error: Error, errorName: string, errorCode: string) => void;
  children?: ReactNode;
  historyPaths?: string[];
}

export interface ComponentErrorBoundaryState {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCode: string | null;
}

class ComponentErrorBoundary extends Component<ComponentErrorBoundaryProps, ComponentErrorBoundaryState> {
  public state: ComponentErrorBoundaryState = {
    error: null,
    errorInfo: null,
    errorCode: null,
  };

  clearState () {
    this.setState({
      error: null,
      errorInfo: null,
      errorCode: null,
    });
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorCode = generateRawErrorCode(true, error.stack);
    const errorName = error.toString().split(' ')[0].replace(':', '');

    this.setState({
      error,
      errorInfo,
      errorCode,
    });

    postJsError({
      name: errorName,
      message: error.toString(),
      source: 'ErrorScreen',
      stack: errorInfo.componentStack + this.getSessionRoutesForStack(),
      userAgent: window?.navigator?.userAgent,
      userAgentLang: window?.navigator?.language,
      lang: getCurrentLanguage(),
      path: window?.location?.href,
      innerWidthHeight: `${window?.innerWidth} x ${window?.innerHeight}`,
      code: errorCode,
    }).catch(reason => console.warn(reason));

    if (this.props.onComponentDidCatch != null) {
      this.props.onComponentDidCatch(error, errorName, errorCode);
    }
  }

  private getSessionRoutesForStack() {
    const { historyPaths } = this.props;

    return historyPaths
        ? `\n session routes:\n${historyPaths.join(Delimiters.ARROW_WITH_SPACES)}`
        : '\n session routes unknown';
  }

  public render() {
    if (this.state.errorInfo) {
      return this.props.fallbackErrorComponent(this.state, () => this.clearState());
    }

    return this.props.children;
  }
}

export default ComponentErrorBoundary;
